import type {
  TurnResponse,
  TurnState,
  WbAgentClient,
} from '@wandb/weave/lib/WbAgent/WbAgentClient';
import {TERMINAL_STATES} from '@wandb/weave/lib/WbAgent/WbAgentClient';

import {extractErrorMessage} from './errors';
import type {RuntimeEvent} from './events';
import type {AgentRuntime, ResumeTurnParams, RunTurnParams} from './runtime';
import {terminalEventForTurnState, TurnEventExtractor} from './turn';

export type GetParentNodeId = (nodeId: string) => string | null;

interface ThreadMapping {
  rootTurnId: string;
  /**
   * turnIds records completed turn IDs. The backend rejects child messages
   * parented to errored or cancelled turns, so only completed turns are
   * valid parents for `resolveParentTurnId`.
   */
  turnIds: Set<string>;
}

const POLL_INITIAL_MS = 1000;
const POLL_SETTLED_MS = 3000;
const POLL_MAX_MS = 5000;
const POLL_FACTOR = 1.5;

const INACTIVITY_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * Poll-based runtime backed by the wb_agent service.
 *
 * Creates turns via `POST /turns`, `polls GET /turns/{id}` with adaptive
 * backoff, and synthesizes {@link RuntimeEvent}s from turn snapshots.
 */
export class WBAgentRuntime implements AgentRuntime {
  private threads = new Map<string, ThreadMapping>();
  private controllers = new Map<string, AbortController>();

  constructor(
    private readonly client: WbAgentClient,
    private readonly getParentNodeId: GetParentNodeId
  ) {}

  cancel(threadId: string): void {
    const controller = this.controllers.get(threadId);
    if (controller) {
      controller.abort();
      this.controllers.delete(threadId);
    }
  }

  async *resumeTurn({
    turnId,
    threadId,
    signal: externalSignal,
    initialSnapshot,
  }: ResumeTurnParams): AsyncGenerator<RuntimeEvent, void, undefined> {
    const {signal, finish} = this.startOperation(threadId, externalSignal);

    const runId = crypto.randomUUID();
    yield {type: 'run-started', runId, threadId};

    const extractor = new TurnEventExtractor(turnId);
    extractor.extract(initialSnapshot);

    try {
      if (isTerminal(initialSnapshot.state)) {
        const event = terminalEventForTurnState(
          initialSnapshot.state,
          runId,
          initialSnapshot.final_output
        );
        if (event) {
          yield event;
        }

        if (initialSnapshot.state === 'completed') {
          this.threads.get(threadId)?.turnIds.add(turnId);
        }

        return;
      }

      yield* this.pollTurn(turnId, threadId, runId, signal, extractor);

      if (signal.aborted) {
        await this.cancelTurn(turnId);
        yield {type: 'run-cancelled', runId};
      }
    } catch (err) {
      if (signal.aborted) {
        await this.cancelTurn(turnId);
        yield {type: 'run-cancelled', runId};
      } else {
        yield {
          type: 'run-error',
          message: extractErrorMessage(err),
          runId,
        };
      }
    } finally {
      finish();
    }
  }

  async *runTurn({
    threadId,
    userMessage,
    parentId,
    signal: externalSignal,
  }: RunTurnParams): AsyncGenerator<RuntimeEvent, void, undefined> {
    const {signal, finish} = this.startOperation(threadId, externalSignal);

    // NOTE: Client-only; wb_agent doesn't have run IDs. This helps uniquely
    // identify conversational turns within the frontend chat state.
    const runId = crypto.randomUUID();
    yield {type: 'run-started', runId, threadId};

    let turnId: string | null = null;
    try {
      const result = await this.createTurn(
        threadId,
        userMessage,
        parentId ?? null,
        runId,
        signal
      );
      if (!result.ok) {
        yield result.event;
        return;
      }

      turnId = result.turnId;
      yield {type: 'turn-created', rootTurnId: result.rootTurnId};

      if (signal.aborted) {
        await this.cancelTurn(turnId);
        yield {type: 'run-cancelled', runId};
        return;
      }

      yield* this.pollTurn(turnId, threadId, runId, signal);

      // `pollTurn` breaks (without yielding) on abort; handle it here.
      if (signal.aborted) {
        await this.cancelTurn(turnId);
        yield {type: 'run-cancelled', runId};
      }
    } catch (err) {
      if (signal.aborted) {
        if (turnId) {
          await this.cancelTurn(turnId);
        }

        yield {type: 'run-cancelled', runId};
      } else {
        yield {
          type: 'run-error',
          message: extractErrorMessage(err),
          runId,
        };
      }
    } finally {
      finish();
    }
  }

  seedThread(threadId: string, rootTurnId: string, turnIds: string[]): void {
    this.threads.set(threadId, {
      rootTurnId,
      turnIds: new Set(turnIds),
    });
  }

  /**
   * startOperation cancels any previous in-flight operation for the thread and
   * creates a new internal `AbortController`. If the external signal aborts,
   * the internal controller is also aborted.
   *
   * Returns the merged signal and a scoped `finish` callback. The callback
   * only removes the controller if it hasn't been replaced by a newer
   * operation, preventing a stale finally-block from deleting a successor's
   * controller.
   */
  private startOperation(
    threadId: string,
    externalSignal: AbortSignal
  ): {signal: AbortSignal; finish: () => void} {
    this.cancel(threadId);

    const controller = new AbortController();
    this.controllers.set(threadId, controller);

    if (externalSignal.aborted) {
      controller.abort();
    } else {
      externalSignal.addEventListener('abort', () => controller.abort(), {
        once: true,
      });
    }

    return {
      signal: controller.signal,
      finish: () => {
        if (this.controllers.get(threadId) === controller) {
          this.controllers.delete(threadId);
        }
      },
    };
  }

  private async createTurn(
    threadId: string,
    userMessage: string | undefined,
    parentId: string | null,
    runId: string,
    signal: AbortSignal
  ): Promise<
    | {ok: true; turnId: string; rootTurnId: string}
    | {ok: false; event: RuntimeEvent}
  > {
    const parentTurnId = this.resolveParentTurnId(threadId, parentId);

    if (signal.aborted) {
      return {ok: false, event: {type: 'run-cancelled', runId}};
    }

    const turnResponse = await this.client.post(
      '/turns',
      {
        entity: this.client.entity,
        parent_turn_id: parentTurnId,
        project: this.client.project,
        user_prompt: userMessage ?? '',
      },
      undefined,
      signal
    );

    if (!this.threads.has(threadId)) {
      this.threads.set(threadId, {
        rootTurnId: turnResponse.root_turn_id,
        turnIds: new Set(),
      });
    }

    return {
      ok: true,
      rootTurnId: turnResponse.root_turn_id,
      turnId: turnResponse.id,
    };
  }

  /**
   * Polls `GET /turns/{id}` with adaptive backoff until a terminal state is
   * reached. Yields content events and terminal events. On abort, returns
   * silently (caller handles cancellation).
   */
  private async *pollTurn(
    turnId: string,
    threadId: string,
    runId: string,
    signal: AbortSignal,
    extractor?: TurnEventExtractor
  ): AsyncGenerator<RuntimeEvent, void, undefined> {
    extractor ??= new TurnEventExtractor(turnId, {
      // NOTE: User messages are optimistically added to thread state, so drop
      // them for in-progress turns.
      skipUserMessages: true,
    });

    let hasReceivedData = false;
    let interval = POLL_INITIAL_MS;
    let lastProgressAt = Date.now();
    let lastSnapshotKey = '';

    while (true) {
      if (signal.aborted) {
        break;
      }

      if (Date.now() - lastProgressAt > INACTIVITY_TIMEOUT_MS) {
        yield* this.cancelAndError(
          turnId,
          runId,
          'No activity from the agent for 5 minutes. Please try again.'
        );
        return;
      }

      await sleep(interval, signal);
      if (signal.aborted) {
        break;
      }

      const turn = await this.client.get<TurnResponse>(
        `/turns/${turnId}`,
        undefined,
        signal
      );

      const snapshotKey = `${turn.state}:${(turn.messages ?? []).length}:${(turn.tool_calls ?? []).length}`;
      if (snapshotKey !== lastSnapshotKey) {
        lastSnapshotKey = snapshotKey;
        lastProgressAt = Date.now();

        if (
          (turn.messages?.length ?? 0) > 0 ||
          (turn.tool_calls?.length ?? 0) > 0
        ) {
          hasReceivedData = true;
        }

        interval = hasReceivedData ? POLL_SETTLED_MS : POLL_INITIAL_MS;
      } else {
        interval = Math.max(
          Math.min(interval * POLL_FACTOR, POLL_MAX_MS),
          hasReceivedData ? POLL_SETTLED_MS : POLL_INITIAL_MS
        );
      }

      if (
        turn.expires_at &&
        new Date(turn.expires_at).getTime() <= Date.now()
      ) {
        yield* this.cancelAndError(
          turnId,
          runId,
          'The request expired before completing. Please try again.'
        );
        return;
      }

      const events = extractor.extract(turn);
      for (const event of events) {
        yield event;
      }

      const terminalEvent = terminalEventForTurnState(
        turn.state,
        runId,
        turn.final_output
      );
      if (terminalEvent) {
        // Only completed turns are valid parents. The backend rejects child
        // messages parented to errored or cancelled turns, so skipping them
        // lets `resolveParentTurnId` walk past to the nearest valid ancestor.
        if (turn.state === 'completed') {
          this.threads.get(threadId)?.turnIds.add(turnId);
        }
        yield terminalEvent;
        return;
      }
    }
  }

  /**
   * cancelAndError is a best-effort attempt to cancel the turn, then yield a
   * `run-error` event.
   */
  private async *cancelAndError(
    turnId: string,
    runId: string,
    message: string
  ): AsyncGenerator<RuntimeEvent, void, undefined> {
    await this.cancelTurn(turnId);
    yield {type: 'run-error', runId, message};
  }

  /**
   * resolveParentTurnId finds the wb_agent turn ID for the specified message
   * ID `parentId`. Walks up the message tree (via `getParentNodeId`) until a
   * node whose ID is in the node-to-turn mapping is found, if one exists.
   */
  private resolveParentTurnId(
    threadId: string,
    parentId: string | null
  ): string | null {
    if (!parentId) {
      return null;
    }

    const mapping = this.threads.get(threadId);
    if (!mapping) {
      return null;
    }

    const visited = new Set<string>();

    let currentId: string | null = parentId;
    while (currentId && !visited.has(currentId)) {
      const turnId = parseTurnId(currentId);
      if (turnId && mapping.turnIds.has(turnId)) {
        return turnId;
      }
      visited.add(currentId);
      currentId = this.getParentNodeId(currentId);
    }

    return null;
  }

  private async cancelTurn(turnId: string): Promise<void> {
    try {
      await this.client.post<TurnResponse>(
        `/turns/${turnId}/cancel`,
        undefined
      );
    } catch {
      // Best-effort cancel; there's nothing to do here.
    }
  }
}

function isTerminal(state: TurnState): boolean {
  return TERMINAL_STATES.includes(state);
}

function parseTurnId(nodeId: string): string | null {
  const idx = nodeId.indexOf(':group:');
  return idx > 0 ? nodeId.slice(0, idx) : null;
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise(resolve => {
    if (signal.aborted) {
      resolve();
      return;
    }

    const onAbort = () => {
      clearTimeout(timer);
      resolve();
    };

    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);

    signal.addEventListener('abort', onAbort, {once: true});
  });
}
